/**
 * Whether the inbox is drawn folded by correspondent, remembered per browser.
 *
 * A habit rather than a decision, like the density and the reading pane next
 * door: an account manager with twenty named customers wants the folded folder
 * every morning, and a support desk answering strangers wants the flat one.
 * Re-picking it after every reload is a small daily tax.
 *
 * The INBOX's setting and nothing else's, stored under its own key. The call
 * log has the same toggle and the same button, but the two screens fold on
 * different identities - a phone number there, a client or an address here -
 * and an operator who wants one is not saying anything about the other. The
 * density is shared between the screens precisely because it says nothing about
 * what the rows mean; this does.
 *
 * Plain `useState` over a module store, unlike `density.ts`: this preference
 * has exactly one reader, the list pane. A module store buys reach across the
 * tree, and there is no tree to reach across.
 */
import { useCallback, useState } from "react";

const KEY = "crm.mail.grouped";

/** What this browser last left on. Never throws - a list that failed to render
 *  because a stored preference was malformed would be far worse than a toggle
 *  that starts in the wrong position. */
function read(): boolean {
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

/** The setting plus the switch, for the pane that owns the toggle. */
export function useGroupedMail(): [boolean, () => void] {
  const [on, setOn] = useState<boolean>(read);

  const toggle = useCallback(() => {
    setOn((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(KEY, next ? "1" : "0");
      } catch {
        // A browser with storage switched off still gets the toggle; it simply
        // forgets it on the next reload.
      }
      return next;
    });
  }, []);

  return [on, toggle];
}
